import cellTypeMap from '@redviking/argonaut-core-ui/src/applet/design/cells/cellTypeMap';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';

// NOTE: we want to filter out argo cells that will not physically be on the screen so we have to invert the result of incorporeal to only get corporeal cells
export const isCellReal = (cell: Latest.Screen.Cells.AppletCell): boolean => {
    const incorporeal = cellTypeMap[cell.type].incorporeal;
    if (incorporeal === undefined) {
        return true;
    } else if (typeof incorporeal === 'boolean') {
        return !incorporeal;
    } else {
        return !incorporeal(cell);
    }
};

export function getUnlinkedCells (opts: {
    screen: Latest.Screen.Config,
}): {
    argoCell: Latest.Screen.Cells.AppletCell;
    usesInAspectRatios: Latest.Screen.Designer.GridLayoutAspectRatio[];
}[] {
    const allLinkedGridCellsFromEnabledAspectRatios = Object.values(opts.screen.gridLayoutAspectRatios).filter(gLAR => gLAR.enabled).flatMap(gridLayout => gridLayout.linkedGridCells);
    const nonEnabledGridLayouts = Object.entries(opts.screen.gridLayoutAspectRatios).filter(([ _, gLAR ]) => !gLAR.enabled);
    const ret: {
        argoCell: Latest.Screen.Cells.AppletCell;
        usesInAspectRatios: Latest.Screen.Designer.GridLayoutAspectRatio[];
    }[] = [];
    opts.screen.cells.filter(c => isCellReal(c)).forEach(cell => {
        if (!allLinkedGridCellsFromEnabledAspectRatios.find(lgc => lgc.type === 'regular' && lgc.additionalCellProperties.argoCellId === cell.id)) {
            const item: {
                argoCell: Latest.Screen.Cells.AppletCell;
                usesInAspectRatios: Latest.Screen.Designer.GridLayoutAspectRatio[];
            } = {
                argoCell: cell,
                usesInAspectRatios: [],
            };
            for (const [ aspectRatio, gLAR ] of nonEnabledGridLayouts) {
                if (gLAR.linkedGridCells.find(lgc => lgc.type === 'regular' && lgc.additionalCellProperties.argoCellId === cell.id)) {
                    item.usesInAspectRatios.push(aspectRatio as Latest.Screen.Designer.GridLayoutAspectRatio);
                }
            }
            ret.push(item);
        }
    });
    return ret;
}

export function getAllCellsWithLinkStatus (opts: {
    screen: Latest.Screen.Config,
}): {
    argoCell: Latest.Screen.Cells.AppletCell;
    linked: boolean;
}[] {
    const allLinkedGridCellsFromEnabledAspectRatios = Object.values(opts.screen.gridLayoutAspectRatios).filter(gLAR => gLAR.enabled).flatMap(gridLayout => gridLayout.linkedGridCells);
    return opts.screen.cells.filter(c => !cellTypeMap[c.type].incorporeal).map(cell => ({
        argoCell: cell,
        linked: Boolean(allLinkedGridCellsFromEnabledAspectRatios.find(lgc => lgc.type === 'regular' && lgc.additionalCellProperties.argoCellId === cell.id)),
    }));
}

export function getUnlinkedCell (opts: {
    screen: Latest.Screen.Config,
}): Latest.Screen.Config['cells'][number] | null {
    const allLinkedGridCells = Object.values(opts.screen.gridLayoutAspectRatios).filter(gLAR => gLAR.enabled).flatMap(gridLayout => gridLayout.linkedGridCells);
    const unlinkedCell = opts.screen.cells.filter(c => isCellReal(c)).find(cell => !allLinkedGridCells.find(lgc => lgc.type === 'regular' && lgc.additionalCellProperties.argoCellId === cell.id));
    return unlinkedCell || null;
}
